'use client';

import { useEffect, useState } from 'react';
import { Check } from 'lucide-react';
import { useHandleError } from '@/hooks/useHandleError';
import { apiFetch } from '@/lib/api/apiClient';
import { useAuthStore } from '@/stores/useAuthStore';
import { useModalStore } from '@/stores/useModalStore';
import { useToastStore } from '@/stores/useToastStore';
import { genderList } from '@/constants/constants';
import { UserProfileItem } from '@/types/users';
import ButtonGAdaptive from '@/components/ButtonGAdaptive';
import ErrorBox from '@/components/ErrorBox';
import LabelBadge from '@/components/LabelBadge';
import SectionTitle from '@/components/SectionTitle';
import FormFieldLabel from '@/components/ui/form/FormFieldLabel';
import FormInputWithLabel from '@/components/ui/form/FormInputWithLabel';
import FormSectionTitle from '@/components/ui/form/FormSectionTitle';
import FormSelect from '@/components/ui/form/FormSelect';
import { Birthdate, FormSelectBirthdate } from '@/components/ui/form/FormSelectBirthdate';

export default function DashboardPersonalProfile() {
    const userAuth = useAuthStore((s) => s.userAuth);
    const setUserAuth = useAuthStore((s) => s.setUserAuth);
    const { handleError, errorMessage, clearError } = useHandleError();
    const closeModal = useModalStore((state) => state.closeModal);
    const showToastAsync = useToastStore((s) => s.showToastAsync);
    const [isProcessing, setIsProcessing] = useState(false);
    const [formData, setFormData] = useState<UserProfileItem>({
        name: '',
        name_kana: '',
        nickname: '',
        self_introduction: '',
        birthday: '',
        gender_cd: '',
        gender_name: '',
    });
    const [birthdate, setBirthdate] = useState<Birthdate>({
        year: '',
        month: '',
        day: '',
    });

    useEffect(() => {
        const day = String(birthdate.day).padStart(2, '0');
        const month = String(birthdate.month).padStart(2, '0');
        const newBirthday = `${birthdate.year}${month}${day}`;
        handleFormDataChange('birthday', newBirthday);
    }, [birthdate]);

    const handleSubmit = async () => {
        clearError();
        setIsProcessing(true);
        try {
            await apiFetch(`/users/me/profile`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });
            closeModal();
            if (userAuth) {
                setUserAuth({
                    schoomyersId: userAuth?.schoomyersId ?? null,
                    nickname: formData.nickname,
                    email: userAuth?.email ?? null,
                    selfIntroduction: formData.self_introduction,
                });
                await showToastAsync('プロフィールの設定が完了しました', 'success');
            }
        } catch (err: unknown) {
            setIsProcessing(false);
            handleError(err);
        }
    };

    useEffect(() => {
        (async () => {
            try {
                const data = await apiFetch(`/users/me/profile`);
                setFormData((prev) => ({
                    ...prev,
                    ...data,
                }));
                if (data.birthday) {
                    setBirthdate({
                        year: data.birthday.slice(0, 4),
                        month: data.birthday.slice(4, 6),
                        day: data.birthday.slice(6),
                    });
                }
            } catch (err: unknown) {
                handleError(err);
            }
        })();
    }, []);

    const handleFormDataChange = (target: string, value: string) => {
        setFormData({
            ...formData,
            [target]: value ?? '',
        });
    };

    useEffect(() => {
        if (!errorMessage?.errors?.length) return;
        const firstError = errorMessage.errors[0];
        const fieldName = Array.isArray(firstError.loc) ? firstError.loc[1] : String(firstError.loc);
        const element = document.getElementById(`${fieldName}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [errorMessage]);

    return (
        <div className="flex flex-col w-full md:w-[98%] section-4-b bg-color-white-base">
            <div className="flex w-full items-center">
                <div className="flex flex-grow justify-start items-center w-full">
                    <SectionTitle title={`Profile`} />
                </div>
            </div>
            <div className="flex flex-col w-full">
                <div className="section-spacer-inner-y"></div>
                <div className="flex justify-start items-center w-full py-4">
                    <FormSectionTitle title="公開プロフィール" />
                </div>
                <div className="flex flex-col items-start justify-start w-full py-4">
                    <div id="nickname" className="flex flex-col items-center w-full">
                        <div className="flex justify-start items-center w-full mb-4">
                            <FormFieldLabel title="ニックネーム" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2">
                            <ErrorBox errors={errorMessage?.errors} field="nickname" />
                        </div>
                        <div className="flex w-full">
                            <FormInputWithLabel
                                id="nickname"
                                name="nickname"
                                label="20文字以内"
                                type="text"
                                maxLength={20}
                                autoComplete="off"
                                value={formData.nickname}
                                onChange={(e) => handleFormDataChange('nickname', e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="section-spacer-inner-y"></div>
                <div className="flex justify-start items-center w-full py-4">
                    <FormSectionTitle title="非公開プロフィール" />
                </div>
                <div className="flex flex-col items-start justify-start w-full py-4">
                    <div id="name" className="flex flex-col items-center w-full">
                        <div className="flex justify-start items-center w-full mb-4">
                            <FormFieldLabel title="お名前" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2">
                            <ErrorBox errors={errorMessage?.errors} field="name" />
                        </div>
                        <div className="flex w-full">
                            <FormInputWithLabel
                                id="name"
                                name="name"
                                label="20文字以内"
                                type="text"
                                maxLength={20}
                                autoComplete="off"
                                value={formData.name}
                                onChange={(e) => handleFormDataChange('name', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="py-6 md:py-4"></div>
                    <div id="name_kana" className="flex flex-col items-center w-full">
                        <div className="flex justify-start items-center w-full mb-2">
                            <FormFieldLabel title="フリガナ" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2">
                            <ErrorBox errors={errorMessage?.errors} field="name_kana" />
                        </div>
                        <div className="flex w-full">
                            <FormInputWithLabel
                                id="name_kana"
                                name="name_kana"
                                label="20文字以内"
                                type="text"
                                maxLength={20}
                                autoComplete="off"
                                value={formData.name_kana}
                                onChange={(e) => handleFormDataChange('name_kana', e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="py-6 md:py-4"></div>
                    <div id="birthday" className="flex flex-col items-center w-full">
                        <div className="flex justify-start items-center w-full mb-2">
                            <FormFieldLabel title="生年月日" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2">
                            <ErrorBox errors={errorMessage?.errors} field="birthday" />
                        </div>
                        <div className="flex w-full">
                            <div className="flex w-full text-color-base">
                                <FormSelectBirthdate value={birthdate} onChange={setBirthdate} />
                            </div>
                        </div>
                    </div>
                    <div className="py-6 md:py-4"></div>
                    <div id="gender_cd" className="flex flex-col items-center w-full">
                        <div className="flex justify-start items-center w-full mb-2">
                            <FormFieldLabel title="性別" />
                            <LabelBadge type="required" />
                        </div>
                        <div className="flex w-full justify-start my-2">
                            <ErrorBox errors={errorMessage?.errors} field="gender_cd" />
                        </div>
                        <div className="flex w-full relative">
                            <div className="flex w-full text-color-base">
                                <FormSelect
                                    id="gender"
                                    name="gender"
                                    value={formData.gender_cd || ''}
                                    onChange={(e) => handleFormDataChange('gender_cd', e.target.value)}
                                    options={[{ label: '性別を選択してください', value: '' }, ...genderList]}
                                    className="w-1/3"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="flex flex-col items-start justify-start w-full py-2">
                <div className="flex flex-col-reverse md:flex-row justify-center w-full pt-12">
                    <div className="flex-grow"></div>
                    <div className="flex flex-col justify-end items-center">
                        <div className="flex justify-center items-center">
                            <ButtonGAdaptive
                                type="submit"
                                label="設定する"
                                onClick={handleSubmit}
                                description="set up your profile"
                                icon={Check}
                                isProcessing={isProcessing}
                            />
                        </div>
                    </div>
                    <div className="flex-grow"></div>
                </div>
            </div>
        </div>
    );
}
